import { toast } from 'sonner'

// Support dynamic API URL for parallel test execution
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

// Global loading state tracking
let activeRequests = 0
let loadingListeners = []

export const subscribeToLoading = (listener) => {
  loadingListeners.push(listener)
  return () => {
    loadingListeners = loadingListeners.filter(l => l !== listener)
  }
}

export const getIsLoading = () => activeRequests > 0

const notifyListeners = () => {
  const isLoading = activeRequests > 0
  loadingListeners.forEach(listener => listener(isLoading))
}

const startLoading = () => {
  activeRequests++
  notifyListeners()
}

const stopLoading = () => {
  activeRequests = Math.max(0, activeRequests - 1)
  notifyListeners()
}

// Helper to get auth token from localStorage (useUserStore persists there)
const getAuthToken = () => {
  try {
    const userStorage = JSON.parse(localStorage.getItem('user-storage') || '{}')
    return userStorage.state?.authToken || null
  } catch {
    return null
  }
}

// Custom error class for version conflicts
export class VersionConflictError extends Error {
  constructor(message = 'This record was modified by someone else.') {
    super(message)
    this.name = 'VersionConflictError'
    this.status = 409
  }
}

// Helper to redirect to login page with return path as URL parameter
const redirectToLogin = () => {
  const currentPath = window.location.pathname
  // Don't include /login as return path (would cause loop)
  if (currentPath && currentPath !== '/login') {
    window.location.href = `/login?returnTo=${encodeURIComponent(currentPath)}`
  } else {
    window.location.href = '/login'
  }
}

// Helper for authenticated requests
const authFetch = async (endpoint, options = {}) => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('Authentication required')
  }

  startLoading()
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    })

    const data = await response.json()

    // Handle 401 Unauthorized - token invalid or expired
    if (response.status === 401) {
      redirectToLogin()
      throw new Error('Session expired. Please log in again.')
    }

    if (response.status === 409) {
      throw new VersionConflictError(data.error || 'This record was modified by someone else.')
    }

    if (!response.ok) {
      const errorMessage = data.error || 'Request failed'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }

    return data
  } finally {
    stopLoading()
  }
}

// Project API functions

export const fetchProjects = async () => {
  const data = await authFetch('/projects/list', { method: 'POST' })
  return data.projects
}

export const createProject = async (name, settings = {}) => {
  const data = await authFetch('/projects/add', {
    method: 'POST',
    body: JSON.stringify({ name, settings })
  })
  return data.project
}

export const getProject = async (id) => {
  const data = await authFetch('/projects/get', {
    method: 'POST',
    body: JSON.stringify({ id })
  })
  return data.project
}

export const updateProject = async (id, updates, rowVersion) => {
  const data = await authFetch('/projects/update', {
    method: 'POST',
    body: JSON.stringify({ id, rowVersion, ...updates })
  })
  return data.project
}

export const deleteProject = async (id, rowVersion) => {
  const data = await authFetch('/projects/delete', {
    method: 'POST',
    body: JSON.stringify({ id, rowVersion })
  })
  return data.success
}

// Task API functions

export const fetchTasks = async (projectId) => {
  const data = await authFetch('/projects/tasks/list', {
    method: 'POST',
    body: JSON.stringify({ projectId })
  })
  return data.tasks
}

export const fetchMyTasks = async () => {
  const data = await authFetch('/projects/tasks/my', { method: 'POST' })
  // API now returns { projects: [...] } with each project containing statuses, members, and tasks
  return data
}

export const createTask = async (projectId, taskData) => {
  const data = await authFetch('/projects/tasks/add', {
    method: 'POST',
    body: JSON.stringify({ projectId, ...taskData })
  })
  return data.task
}

export const updateTask = async (projectId, taskId, updates, rowVersion) => {
  const data = await authFetch('/projects/tasks/update', {
    method: 'POST',
    body: JSON.stringify({ projectId, taskId, rowVersion, ...updates })
  })
  return data.task
}

export const deleteTask = async (projectId, taskId, rowVersion) => {
  const data = await authFetch('/projects/tasks/delete', {
    method: 'POST',
    body: JSON.stringify({ projectId, taskId, rowVersion })
  })
  return data.success
}

export const batchUpdateTasks = async (projectId, updates) => {
  // updates is an array of { id, sortOrder, parentTaskId, rowVersion, ... }
  // Returns { success, tasks } on success
  // Returns { success: false, error: 'VERSION_CONFLICT', conflicts: [...] } on conflict
  const data = await authFetch('/projects/tasks/batch', {
    method: 'POST',
    body: JSON.stringify({ projectId, updates })
  })
  return data
}

// User API functions

export const getCurrentUser = async () => {
  const data = await authFetch('/users/me', { method: 'POST' })
  return data.user
}

export const updateUserProfile = async (updates, rowVersion) => {
  const data = await authFetch('/users/update', {
    method: 'POST',
    body: JSON.stringify({ rowVersion, ...updates })
  })
  return data.user
}

export const registerUser = async (inviteCode, password, fullName) => {
  startLoading()
  try {
    const response = await fetch(`${API_URL}/users/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ inviteCode, password, fullName })
    })

    const data = await response.json()

    if (!response.ok) {
      const errorMessage = data.error || 'Registration failed'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }

    return data
  } finally {
    stopLoading()
  }
}

export const deleteSelf = async () => {
  const data = await authFetch('/users/delete-self', {
    method: 'POST'
  })
  return data
}

export const resetInviteCode = async (userId) => {
  const data = await authFetch('/users/reset-invite', {
    method: 'POST',
    body: JSON.stringify({ userId })
  })
  return data
}

// Team Management API functions

export const inviteUser = async (projectId, email, permission) => {
  const data = await authFetch('/users/invite', {
    method: 'POST',
    body: JSON.stringify({ projectId, email, permission })
  })
  return data
}

export const getProjectMembers = async (projectId) => {
  const data = await authFetch('/projects/members/list', {
    method: 'POST',
    body: JSON.stringify({ projectId })
  })
  return data.members
}

export const updateMemberPermission = async (projectId, userId, permission) => {
  const data = await authFetch('/projects/members/update', {
    method: 'POST',
    body: JSON.stringify({ projectId, userId, permission })
  })
  return data
}

export const removeMember = async (projectId, userId) => {
  const data = await authFetch('/projects/members/remove', {
    method: 'POST',
    body: JSON.stringify({ projectId, userId })
  })
  return data
}

// Attachment API functions

// Upload files (multipart/form-data)
// Note: projectId and taskId go as query params, files in FormData
export const uploadAttachments = async (projectId, taskId, files) => {
  const token = getAuthToken()
  if (!token) throw new Error('Authentication required')

  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }

  startLoading()
  try {
    const response = await fetch(
      `${API_URL}/attachments/upload?projectId=${projectId}&taskId=${taskId}`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      }
    )

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Upload failed')
    }

    return response.json()
  } finally {
    stopLoading()
  }
}

// Fetch attachment list for a task
export const fetchAttachments = async (projectId, taskId) => {
  const data = await authFetch('/attachments/list', {
    method: 'POST',
    body: JSON.stringify({ projectId, taskId })
  })
  return data.attachments
}

// Delete an attachment
export const deleteAttachment = async (projectId, attachmentId) => {
  const data = await authFetch('/attachments/delete', {
    method: 'POST',
    body: JSON.stringify({ projectId, attachmentId })
  })
  return data.success
}

// Get download URL (for anchor href)
export const getAttachmentDownloadUrl = (projectId, attachmentId) => {
  const token = getAuthToken()
  return `${API_URL}/attachments/download/${projectId}/${attachmentId}?token=${token}`
}

// Get thumbnail URL (for img src)
export const getAttachmentThumbnailUrl = (projectId, attachmentId) => {
  const token = getAuthToken()
  return `${API_URL}/attachments/thumbnail/${projectId}/${attachmentId}?token=${token}`
}

// Notification API functions

export const fetchNotifications = async (limit = 50) => {
  const data = await authFetch('/notifications/list', {
    method: 'POST',
    body: JSON.stringify({ limit })
  })
  return data
}

export const fetchUnreadNotificationCount = async () => {
  const data = await authFetch('/notifications/unread-count', {
    method: 'POST'
  })
  return data
}

export const markNotificationsRead = async ({ notificationIds, all }) => {
  const data = await authFetch('/notifications/mark-read', {
    method: 'POST',
    body: JSON.stringify({ notificationIds, all })
  })
  return data
}

// Task Watch API functions

export const fetchWatchedTasks = async (projectId) => {
  const data = await authFetch('/tasks/watching', {
    method: 'POST',
    body: JSON.stringify({ projectId })
  })
  return data
}

export const watchTask = async (taskId) => {
  const data = await authFetch('/tasks/watch', {
    method: 'POST',
    body: JSON.stringify({ taskId })
  })
  return data
}

export const unwatchTask = async (taskId) => {
  const data = await authFetch('/tasks/unwatch', {
    method: 'POST',
    body: JSON.stringify({ taskId })
  })
  return data
}

// Export project tasks to CSV
export const exportProjectToCSV = async (projectId) => {
  const token = getAuthToken()
  if (!token) throw new Error('Authentication required')

  startLoading()
  try {
    const response = await fetch(`${API_URL}/projects/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ projectId })
    })

    if (response.status === 401) {
      redirectToLogin()
      throw new Error('Session expired')
    }

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Export failed')
    }

    // Get filename from Content-Disposition header
    const disposition = response.headers.get('Content-Disposition')
    const filenameMatch = disposition?.match(/filename="(.+)"/)
    const filename = filenameMatch ? filenameMatch[1] : 'tasks.csv'

    // Trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  } finally {
    stopLoading()
  }
}

// Import project tasks from CSV
// When confirm=false, validates the CSV and returns { valid, rowCount, errors, warnings }
// When confirm=true, performs the import and returns { imported, count }
export const importProjectFromCSV = async (projectId, csvContent, confirm = false) => {
  const data = await authFetch('/projects/import', {
    method: 'POST',
    body: JSON.stringify({ projectId, csvContent, confirm })
  })
  return data
}

// OAuth API functions

// Helper for unauthenticated requests (OAuth flows don't require auth token)
const unauthFetch = async (endpoint, body = {}) => {
  startLoading()
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })

    const data = await response.json()

    if (!response.ok) {
      const errorMessage = data.error || 'Request failed'
      throw new Error(errorMessage)
    }

    return data
  } finally {
    stopLoading()
  }
}

// Get available OAuth providers
export const getAuthProviders = async () => {
  return unauthFetch('/auth/providers', {})
}

// Initialize Microsoft OAuth flow
export const initMicrosoftAuth = async (returnTo = '/projects') => {
  return unauthFetch('/auth/microsoft/init', { returnTo })
}

// Complete Microsoft OAuth authentication
export const completeMicrosoftAuth = async (code, state) => {
  return unauthFetch('/auth/microsoft/callback', { code, state })
}

// Get user's linked OAuth providers (for profile page)
export const getLinkedProviders = async () => {
  return authFetch('/auth/providers/linked', { method: 'POST' })
}

// Initialize linking Microsoft to existing account
export const initLinkMicrosoft = async () => {
  return authFetch('/auth/link/microsoft', { method: 'POST' })
}

// Complete linking Microsoft account
export const completeLinkMicrosoft = async (code, state) => {
  return unauthFetch('/auth/link/microsoft/callback', { code, state })
}

// Unlink Microsoft account
export const unlinkMicrosoft = async () => {
  return authFetch('/auth/unlink/microsoft', { method: 'POST' })
}

// Initialize Google OAuth flow
export const initGoogleAuth = async (returnTo = '/projects') => {
  return unauthFetch('/auth/google/init', { returnTo })
}

// Complete Google OAuth authentication
export const completeGoogleAuth = async (code, state) => {
  return unauthFetch('/auth/google/callback', { code, state })
}

// Initialize linking Google to existing account
export const initLinkGoogle = async () => {
  return authFetch('/auth/link/google', { method: 'POST' })
}

// Complete linking Google account
export const completeLinkGoogle = async (code, state) => {
  return unauthFetch('/auth/link/google/callback', { code, state })
}

// Unlink Google account
export const unlinkGoogle = async () => {
  return authFetch('/auth/unlink/google', { method: 'POST' })
}

// Signup API function (unauthenticated)

export const signupUser = async (email) => {
  startLoading()
  try {
    const response = await fetch(`${API_URL}/users/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    // Special handling for rate limiting
    if (response.status === 429) {
      const errorMessage = 'Too many requests. Please try again later.'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }

    const data = await response.json()

    if (!response.ok) {
      const errorMessage = data.error || 'Signup request failed'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }

    return data
  } finally {
    stopLoading()
  }
}

// Email-to-Task API functions

/**
 * Get email-to-task server configuration
 * @returns {Promise<{configured: boolean, emailDomain: string|null}>}
 */
export const getEmailToTaskConfig = async () => {
  const data = await authFetch('/projects/email-to-task/config', { method: 'POST' })
  return data
}

/**
 * Generate a unique mailbox name for a project
 * @param {number} projectId - The project ID
 * @returns {Promise<{mailboxName: string}>}
 */
export const generateMailboxName = async (projectId) => {
  const data = await authFetch('/projects/generate-mailbox-name', {
    method: 'POST',
    body: JSON.stringify({ projectId })
  })
  return data
}

/**
 * Check if a mailbox name is available
 * @param {string} mailboxName - The mailbox name to check
 * @param {number} projectId - The current project ID (to exclude from check)
 * @returns {Promise<{available: boolean, suggestion?: string}>}
 */
export const checkMailboxNameAvailability = async (mailboxName, projectId) => {
  const data = await authFetch('/projects/check-mailbox-name', {
    method: 'POST',
    body: JSON.stringify({ mailboxName, projectId })
  })
  return data
}

// Comment API functions

export const fetchComments = async (projectId, taskId) => {
  const data = await authFetch('/comments/list', {
    method: 'POST',
    body: JSON.stringify({ projectId, taskId })
  })
  return data.comments
}

export const addComment = async (projectId, taskId, content, durationSeconds = null) => {
  const body = { projectId, taskId }
  if (content) body.content = content
  if (durationSeconds) body.durationSeconds = durationSeconds
  const data = await authFetch('/comments/add', {
    method: 'POST',
    body: JSON.stringify(body)
  })
  return data.comment
}

export const updateComment = async (projectId, commentId, content, rowVersion, durationSeconds = null) => {
  const body = { projectId, commentId, content, rowVersion }
  if (durationSeconds !== null) body.durationSeconds = durationSeconds
  const data = await authFetch('/comments/update', {
    method: 'POST',
    body: JSON.stringify(body)
  })
  return data.comment
}

export const deleteComment = async (projectId, commentId, rowVersion) => {
  const data = await authFetch('/comments/delete', {
    method: 'POST',
    body: JSON.stringify({ projectId, commentId, rowVersion })
  })
  return data.success
}

// Timer API functions

export const startTimer = async (projectId, taskId) => {
  const data = await authFetch('/comments/start-timer', {
    method: 'POST',
    body: JSON.stringify({ projectId, taskId })
  })
  return data
}

export const stopTimer = async (projectId, entryId, content = null, rowVersion) => {
  const body = { projectId, entryId, rowVersion }
  if (content) body.content = content
  const data = await authFetch('/comments/stop-timer', {
    method: 'POST',
    body: JSON.stringify(body)
  })
  return data
}

export const getActiveTimer = async (projectId = null) => {
  const body = {}
  if (projectId) body.projectId = projectId
  const data = await authFetch('/comments/active-timer', {
    method: 'POST',
    body: JSON.stringify(body)
  })
  return data
}

// Search tasks across all projects
export const searchTasks = async (query, limit = 20) => {
  const data = await authFetch('/projects/search', {
    method: 'POST',
    body: JSON.stringify({ query, limit })
  })
  return data
}

// Dashboard API functions

export const fetchDashboard = async (projectId) => {
  const data = await authFetch('/projects/dashboard', {
    method: 'POST',
    body: JSON.stringify({ projectId })
  })
  return data
}
